import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Phone,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

interface RegisterFormData {
  name: string;
  email: string;
  country_code: string;
  phone: string;
  password: string;
  confirmPassword: string;
  role: 'rider' | 'driver';
}


const COUNTRY_CODES = [
  { code: '+994', name: 'Azərbaycan', flag: '🇦🇿' },
  { code: '+1', name: 'ABŞ/Kanada', flag: '🇺🇸' },
  { code: '+7', name: 'Rusiya/Qazaxıstan', flag: '🇷🇺' },
  { code: '+90', name: 'Türkiyə', flag: '🇹🇷' },
  { code: '+44', name: 'Böyük Britaniya', flag: '🇬🇧' },
  { code: '+49', name: 'Almaniya', flag: '🇩🇪' },
  { code: '+33', name: 'Fransa', flag: '🇫🇷' },
  { code: '+39', name: 'İtaliya', flag: '🇮🇹' },
  { code: '+34', name: 'İspaniya', flag: '🇪🇸' },
  { code: '+971', name: 'BƏƏ', flag: '🇦🇪' },
  { code: '+374', name: 'Ermənistan', flag: '🇦🇲' },
  { code: '+995', name: 'Gürcüstan', flag: '🇬🇪' },
  { code: '+98', name: 'İran', flag: '🇮🇷' },
];

const schema = yup.object().shape({
  name: yup
    .string()
    .required('Ad tələb olunur')
    .min(2, 'Ad ən azı 2 simvol olmalıdır'),
  email: yup
    .string()
    .email('Düzgün e-poçt ünvanı daxil edin')
    .required('E-poçt ünvanı tələb olunur'),
  country_code: yup
    .string()
    .required('Ölkə kodu seçilməlidir')
    .min(1, 'Ölkə kodu tələb olunur')
    .max(5, 'Ölkə kodu maksimum 5 simvol ola bilər'),
  phone: yup
    .string()
    .required('Telefon nömrəsi tələb olunur')
    .matches(/^[0-9]{9,15}$/, 'Düzgün telefon nömrəsi daxil edin (yalnız rəqəmlər)'),
  password: yup
    .string()
    .min(6, 'Şifrə ən azı 6 simvol olmalıdır')
    .required('Şifrə tələb olunur'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), undefined], 'Şifrələr uyğun gəlmir')
    .required('Şifrənin təsdiqi tələb olunur'),
  role: yup
    .string()
    .oneOf(['rider', 'driver'], 'Rol seçilməlidir')
    .required('Rol tələb olunur'),
});

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register, isLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      country_code: '+994',
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const onSubmit = async (data: RegisterFormData) => {
    setRegisterError(null);

    const result = await register({
      name: data.name,
      email: data.email,
      phone: data.phone,
      password: data.password,
      role: data.role,
      country_code: data.country_code,
    });

    if (result.success) {
      navigate('/app');
    } else {
      setRegisterError(result.message || 'Qeydiyyat zamanı xəta baş verdi');
    }
  };

  return (
    <Card sx={{ maxWidth: 400, mx: 'auto' }}>
      <CardContent>
        <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
          Ozio
        </Typography>
        <Typography variant="body2" gutterBottom align="center" color="text.secondary">
          Yeni hesab yaradın
        </Typography>

        {registerError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {registerError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Ad"
                margin="normal"
                variant="outlined"
                error={!!errors.name}
                helperText={errors.name?.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="E-poçt"
                margin="normal"
                variant="outlined"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Box sx={{ display: 'flex', gap: 1.5, mt: 2, alignItems: 'flex-start' }}>
            <Controller
              name="country_code"
              control={control}
              defaultValue="+994"
              render={({ field }) => (
                <FormControl 
                  sx={{ 
                    minWidth: { xs: 130, sm: 150 },
                    flexShrink: 0,
                  }} 
                  error={!!errors.country_code}
                  margin="normal"
                >
                  <InputLabel id="country-code-label" shrink>Ölkə kodu</InputLabel>
                  <Select
                    {...field}
                    labelId="country-code-label"
                    label="Ölkə kodu"
                    disabled={isLoading}
                    sx={{
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      },
                    }}
                  >
                    {COUNTRY_CODES.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{country.flag}</span>
                          <span>{country.code}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.country_code && (
                    <FormHelperText sx={{ mt: 0.5, mx: 0 }}>{errors.country_code.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name="phone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Telefon nömrəsi"
                  margin="normal"
                  variant="outlined"
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone color="action" />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="0555433690"
                />
              )}
            />
          </Box>

          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Şifrə"
                margin="normal"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Şifrəni təsdiqlə"
                margin="normal"
                variant="outlined"
                type={showConfirmPassword ? 'text' : 'password'}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="role"
            control={control}
            defaultValue="rider"
            render={({ field }) => (
              <FormControl fullWidth margin="normal" error={!!errors.role}>
                <InputLabel id="role-select-label">Rol</InputLabel>
                <Select
                  {...field}
                  labelId="role-select-label"
                  label="Rol"
                  disabled={isLoading}
                >
                  <MenuItem value="rider">Sərnişin</MenuItem>
                  <MenuItem value="driver">Sürücü</MenuItem>
                </Select>
                {errors.role && (
                  <FormHelperText>{errors.role.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2, mb: 2, py: 1.5 }}
            disabled={!isValid || isLoading}
          >
            {isLoading ? 'Qeydiyyat edilir...' : 'Qeydiyyatdan keç'}
          </Button>

          <Box textAlign="center">
            <Typography variant="body2">
              Artıq hesabınız var?{' '}
              <Link component={RouterLink} to="/login" variant="body2">
                Daxil olun
              </Link>
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RegisterPage;
